import { TimeOffPolicy, TimeOffPolicyFieldError } from '../timeoff';
import { IUserData, User } from '../user';
import {
  IEmployeeTimeOff,
  IEmployeeTimeOffBookingBalance,
  IEmployeeTimeOffBookingBalanceData,
  IEmployeeTimeOffData,
  IEmployeeTimeOffEntitlement,
  IEmployeeTimeOffEntitlementAmount,
  IEmployeeTimeOffEntitlementAmountData,
  IEmployeeTimeOffEntitlementData,
  IEmployeeTimeOffEntitlementFieldError,
  IEmployeeTimeOffEntitlementFieldErrorData,
  IEmployeeTimeOffFieldError,
  IEmployeeTimeOffFieldErrorData
} from './employee-timeoff.types';

export class EmployeeTimeOff implements IEmployeeTimeOff {
  readonly id?: number;
  readonly employee?: number;
  readonly calendar?: number;
  readonly template?: number | null;
  readonly approvers?: IUserData[] = [];
  readonly entitlements?: IEmployeeTimeOffEntitlementData[];

  constructor(data?: IEmployeeTimeOffData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }
      if (data.employee) {
        this.employee = data.employee;
      }
      if (data.calendar) {
        this.calendar = data.calendar;
      }
      if (data.template) {
        this.template = data.template;
      }
      if (data.approvers) {
        this.approvers = data.approvers?.map(
          (approver: IUserData) => new User(approver)
        );
      }
      if (data.entitlements) {
        this.entitlements = data.entitlements.map(
          entitlement => new EmployeeTimeOffEntitlement(entitlement)
        );
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class EmployeeTimeOffEntitlementAmount
  implements IEmployeeTimeOffEntitlementAmount {
  readonly effective_date: Date | string;
  readonly expiry_date: Date | string;
  readonly value: number;

  constructor(data: IEmployeeTimeOffEntitlementAmountData) {
    this.effective_date = data.effective_date;
    this.expiry_date = data.expiry_date;
    this.value = data.value;
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class EmployeeTimeOffEntitlement extends TimeOffPolicy
  implements IEmployeeTimeOffEntitlement {
  carryover_balance?: number;
  amounts: IEmployeeTimeOffEntitlementAmount[] = [];
  constructor(data?: IEmployeeTimeOffEntitlementData) {
    super(data);

    if (data) {
      this.carryover_balance = data.carryover_balance ?? 0;

      if (data.amounts) {
        this.amounts = data.amounts.map(
          (amount: IEmployeeTimeOffEntitlementAmountData) =>
            new EmployeeTimeOffEntitlementAmount(amount)
        );
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class EmployeeTimeOffBookingBalance
  implements IEmployeeTimeOffBookingBalance {
  readonly id?: number;
  readonly employee_entitlement?: EmployeeTimeOffEntitlement;
  readonly amount_used: number = 0;
  readonly balance_carried_forward: number = 0;
  readonly total_entitlement: number = 0;
  readonly remaining_balance: number = 0;
  readonly accrual_amount: number = 0;
  readonly carryover_used: number = 0;

  constructor(data?: IEmployeeTimeOffBookingBalanceData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      if (data.employee_entitlement) {
        this.employee_entitlement = new EmployeeTimeOffEntitlement(
          data.employee_entitlement
        );
      }

      this.amount_used = data.amount_used ?? 0;
      this.total_entitlement = data.total_entitlement ?? 0;
      this.balance_carried_forward = data.balance_carried_forward ?? 0;
      this.remaining_balance = data.remaining_balance ?? 0;
      this.accrual_amount = data.accrual_amount ?? 0;
      this.carryover_used = data.carryover_used ?? 0;
    }
  }
  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class EmployeeTimeOffEntitlementFieldError
  extends TimeOffPolicyFieldError
  implements IEmployeeTimeOffEntitlementFieldError {
  readonly carryover_balance: string[] = [];
  constructor(data?: IEmployeeTimeOffEntitlementFieldErrorData) {
    super(data);
    if (data) {
      if (data.carryover_balance) {
        this.carryover_balance = data.carryover_balance;
      }
    }
  }

  clearErrors(): void {
    this.timeoff.splice(0, this.timeoff.length);
    this.carryover_balance.splice(0, this.carryover_balance.length);
  }
}

export class EmployeeTimeOffFieldError implements IEmployeeTimeOffFieldError {
  readonly employee: string[] = [];
  readonly template: string[] = [];
  readonly approvers: string[] = [];
  readonly entitlements: IEmployeeTimeOffEntitlementFieldError[] = [];

  constructor(data?: IEmployeeTimeOffFieldErrorData) {
    if (data) {
      if (data.employee) {
        this.employee = data.employee;
      }
      if (data.template) {
        this.template = data.template;
      }
      if (data.approvers) {
        this.approvers = data.approvers;
      }
      if (data.entitlements) {
        this.entitlements = data.entitlements.map(
          entitlement => new EmployeeTimeOffEntitlementFieldError(entitlement)
        );
      }
    }
  }

  clearErrors(): void {
    this.employee.splice(0, this.employee.length);
    this.template.splice(0, this.template.length);
    this.approvers.splice(0, this.approvers.length);
    this.entitlements.splice(0, this.entitlements.length);
  }

  static isEmployeeTimeOffFieldError(
    data: IEmployeeTimeOffFieldErrorData
  ): boolean {
    if (data.employee && Array.isArray(data.employee)) {
      return true;
    } else if (data.template && Array.isArray(data.template)) {
      return true;
    } else if (data.approvers && Array.isArray(data.approvers)) {
      return true;
    } else if (data.entitlements && Array.isArray(data.entitlements)) {
      return true;
    }
    return false;
  }
}
